import React, { useMemo, useState } from 'react'
import {
    Button,
    Flex,
    PageWrapper,
    PageFooter,
    H1WithBack,
    ProfilePicture,
} from '../../styling/GlobalStyles'
import { useSelector } from 'react-redux'
import { NavBar } from '../../components/NavBar/NavBar'
import { Pagination } from 'components/Pagination/Pagination'
import { RootState } from 'store'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { getParams, handlePagination } from 'utils/helpers'
import useQueryPagination from 'hooks/useQueryPagination'
import { useNavigate } from 'react-router'
import { clearScheduledBreaks, getScheduledBreaks } from 'features/breaks/breaksSlice'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import Avatar from '../../assets/images/profileAvatar.png'
import { BodyText } from 'styling/Typography'
import styled from 'styled-components'
import { ScheduledBreaksModal } from './ScheduledBreaksModal'
import { IGetAllScheduledBreaksResponse } from 'types/api/breaksApi.interface'

const EMPLOYEE_COLUMN_WIDTH = 260
const MINUTE_WIDTH = 8
const DAY_MINUTES = 24 * 60
const TIMELINE_WIDTH = DAY_MINUTES * MINUTE_WIDTH
const HOUR_WIDTH = 60 * MINUTE_WIDTH

const minutesSinceMidnight = (isoDate: string): number => {
    const date = new Date(isoDate)
    return date.getHours() * 60 + date.getMinutes()
}

const formatHourLabel = (hour: number) => `${hour.toString().padStart(2, '0')}:00`

const getBreakSegments = (scheduledBreak: IGetAllScheduledBreaksResponse['breaks'][number]['breaks'][number]) => {
    const startMinute = minutesSinceMidnight(scheduledBreak.scheduledStartTime)
    const endMinuteFromTime = minutesSinceMidnight(scheduledBreak.scheduledEndTime)
    const durationMinutes = Math.max(Number(scheduledBreak.durationMinutes) || 0, 0)

    const endMinuteAbsolute =
        endMinuteFromTime > startMinute
            ? endMinuteFromTime
            : durationMinutes > 0
                ? startMinute + durationMinutes
                : endMinuteFromTime + DAY_MINUTES

    const firstSegmentEnd = Math.min(endMinuteAbsolute, DAY_MINUTES)
    const segments: { start: number, end: number }[] = []

    if (firstSegmentEnd > startMinute) {
        segments.push({ start: startMinute, end: firstSegmentEnd })
    }

    if (endMinuteAbsolute > DAY_MINUTES) {
        segments.push({ start: 0, end: Math.min(endMinuteAbsolute - DAY_MINUTES, DAY_MINUTES) })
    }

    return segments.map(segment => ({
        ...segment,
        left: segment.start * MINUTE_WIDTH,
        width: Math.max((segment.end - segment.start) * MINUTE_WIDTH, 40),
        label: `${String(Math.floor(segment.start / 60)).padStart(2, '0')}:${String(segment.start % 60).padStart(2, '0')} - ${String(Math.floor(segment.end / 60)).padStart(2, '0')}:${String(segment.end % 60).padStart(2, '0')}`,
    }))
}

export const ScheduledBreaks: React.FC = () => {
    const { scheduledBreaks } = useSelector((store: RootState) => store.breaks)
    const [params, setParams] = useState<any>(getParams({ location, limit: 30, ap: { date: new Date() } }))
    const [modal, setModal] = useState({ open: false, value: null })
    const navigate = useNavigate()

    useQueryPagination({ fetchFunctions: [getScheduledBreaks], cleanUpFunctions: [clearScheduledBreaks], params })

    const allBreaks = scheduledBreaks?.breaks ?? []
    const limit = params?.limit ?? 30
    const page = params?.page ?? 1
    const startIndex = (page - 1) * limit
    const paginatedBreaks = allBreaks.slice(startIndex, startIndex + limit)
    const hours = useMemo(() => Array.from({ length: 24 }, (_, hour) => hour), [])

    return (
        <>
            <NavBar defaultValue={params.q} setParams={setParams} limit={30} />
            <PageWrapper>
                <Flex between center mb='16' >
                    <H1WithBack title='Scheduled Breaks' arrow />
                    <Flex gap='1rem' alignEnd>
                        <Button orange onClick={() => navigate('templates')}>
                            Templates
                        </Button>
                        <Button orange onClick={() => navigate('policies')}>
                            Policies
                        </Button>
                        <CalendarInput nomarg labelText='Date' defaultValue={params.date} onChange={date => setParams(prev => ({ ...prev, date }))} />
                    </Flex>
                </Flex>
                {!allBreaks.length ? (
                    <NoItems title='Scheduled Breaks' />
                ) : (
                    <TimelineCard>
                        <TimelineScrollArea>
                            <TimelineHeader>
                                <StickyEmployeeHeader>Employee</StickyEmployeeHeader>
                                <HoursRow>
                                    {hours.map(hour => (
                                        <HourCell key={hour}>{formatHourLabel(hour)}</HourCell>
                                    ))}
                                </HoursRow>
                            </TimelineHeader>
                            <RowsContainer>
                                {paginatedBreaks.map((item) => (
                                    <TimelineRow key={item.user.id}>
                                        <StickyEmployeeCell>
                                            <Flex center gap='1rem'>
                                                <ProfilePicture mr='20'>
                                                    <img
                                                        src={item.user.profileImageUrl || Avatar}
                                                        alt=''
                                                        onError={({ currentTarget }) => {
                                                            currentTarget.onerror = null
                                                            currentTarget.src = Avatar
                                                        }}
                                                    />
                                                </ProfilePicture>
                                                <Flex column>
                                                    <BodyText>{item.user.firstName + ' ' + item.user.lastName}</BodyText>
                                                    <EmployeeMeta>{item.breaks.length} scheduled breaks</EmployeeMeta>
                                                </Flex>
                                            </Flex>
                                        </StickyEmployeeCell>
                                        <TimelineLane>
                                            <HourGrid />
                                            {item.breaks.map((scheduledBreak) =>
                                                getBreakSegments(scheduledBreak).map((segment, index) => (
                                                    <BreakBlock
                                                        key={`${scheduledBreak.breakIndex}-${scheduledBreak.scheduledStartTime}-${index}`}
                                                        left={segment.left}
                                                        width={segment.width}
                                                        title={segment.label}
                                                        onClick={() => setModal({
                                                            open: true, value: {
                                                                userId: item.user.id,
                                                                breakIndex: scheduledBreak.breakIndex,
                                                                newStartDate: scheduledBreak.scheduledStartTime
                                                            }
                                                        })}
                                                    >
                                                        <BreakLabel>{segment.label}</BreakLabel>
                                                    </BreakBlock>
                                                ))
                                            )}
                                        </TimelineLane>
                                    </TimelineRow>
                                ))}
                            </RowsContainer>
                        </TimelineScrollArea>
                    </TimelineCard>
                )}
            </PageWrapper>
            <PageFooter>
                <Pagination
                    setPage={(page) => handlePagination(page, setParams, 30)}
                    pageCount={Math.ceil(allBreaks.length / limit)}
                    forcePage={(params.page ? params.page : 1) - 1}
                />
            </PageFooter>
            {modal.open && <ScheduledBreaksModal closeModal={() => setModal({ open: false, value: null })} value={modal.value} />}
        </>
    )
}

const TimelineCard = styled.div`
    ${({ theme }) => theme.content};
    border: 1px solid ${({ theme }) => theme.tableBorder};
    border-radius: 18px;
    box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
    overflow: hidden;
`

const TimelineScrollArea = styled.div`
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-gutter: stable both-edges;

    ::-webkit-scrollbar {
        height: 12px;
    }

    ::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.colorOrange};
        border-radius: 999px;
    }

    ::-webkit-scrollbar-track {
        background: ${({ theme }) => theme.inputActiveBackground};
    }
`

const TimelineHeader = styled.div`
    display: grid;
    grid-template-columns: ${EMPLOYEE_COLUMN_WIDTH}px ${TIMELINE_WIDTH}px;
    min-width: ${EMPLOYEE_COLUMN_WIDTH + TIMELINE_WIDTH}px;
    border-bottom: 1px solid ${({ theme }) => theme.tableBorder};
    background: ${({ theme }) => theme.inputActiveBackground};
`

const StickyEmployeeHeader = styled.div`
    position: sticky;
    left: 0;
    z-index: 4;
    display: flex;
    align-items: center;
    padding: 1rem 1.25rem;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colorTextFaded};
    background: ${({ theme }) => theme.inputActiveBackground};
    border-right: 1px solid ${({ theme }) => theme.tableBorder};
`

const HoursRow = styled.div`
    display: grid;
    grid-template-columns: repeat(24, ${HOUR_WIDTH}px);
`

const HourCell = styled.div`
    padding: 1rem 0.75rem;
    font-size: 12px;
    font-weight: 700;
    color: ${({ theme }) => theme.textColor};
    border-right: 1px solid ${({ theme }) => theme.tableBorder};
`

const RowsContainer = styled.div`
    min-width: ${EMPLOYEE_COLUMN_WIDTH + TIMELINE_WIDTH}px;
`

const TimelineRow = styled.div`
    display: grid;
    grid-template-columns: ${EMPLOYEE_COLUMN_WIDTH}px ${TIMELINE_WIDTH}px;
    min-height: 92px;
    border-bottom: 1px solid ${({ theme }) => theme.tableBorder};

    :last-child {
        border-bottom: none;
    }
`

const StickyEmployeeCell = styled.div`
    position: sticky;
    left: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    padding: 1rem 1.25rem;
    background: ${({ theme }) => theme.inputActiveBackground};
    border-right: 1px solid ${({ theme }) => theme.tableBorder};
`

const EmployeeMeta = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.colorTextFaded};
`

const TimelineLane = styled.div`
    position: relative;
    height: 92px;
    width: ${TIMELINE_WIDTH}px;
    background:
        linear-gradient(180deg, ${({ theme }) => theme.inputBackground || theme.inputActiveBackground} 0%, ${({ theme }) => theme.inputActiveBackground} 100%),
        repeating-linear-gradient(
            to right,
            ${({ theme }) => theme.tableBorder} 0,
            ${({ theme }) => theme.tableBorder} 1px,
            transparent 1px,
            transparent ${HOUR_WIDTH}px
        );
`

const HourGrid = styled.div`
    position: absolute;
    inset: 0;
    background:
        repeating-linear-gradient(
            to right,
            transparent 0,
            transparent ${HOUR_WIDTH / 2 - 1}px,
            ${({ theme }) => theme.colorTextFaded}25 ${HOUR_WIDTH / 2 - 1}px,
            ${({ theme }) => theme.colorTextFaded}25 ${HOUR_WIDTH / 2}px
        );
`

const BreakBlock = styled.button<{ left: number, width: number }>`
    position: absolute;
    top: 50%;
    left: ${({ left }) => left}px;
    width: ${({ width }) => width}px;
    min-width: 40px;
    height: 50px;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    border: 1px solid ${({ theme }) => theme.colorOrange}55;
    border-radius: 12px;
    background: linear-gradient(135deg, ${({ theme }) => theme.colorOrange} 0%, ${({ theme }) => theme.colorOrangeHover} 100%);
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.18);
    cursor: pointer;
    overflow: hidden;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    z-index: 2;
    color: #ffffff;

    :hover {
        transform: translateY(-50%) translateY(-1px);
        box-shadow: 0 14px 28px rgba(15, 23, 42, 0.24);
        background: linear-gradient(135deg, ${({ theme }) => theme.colorOrangeHover} 0%, ${({ theme }) => theme.colorOrange} 100%);
    }
`

const BreakLabel = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 11px;
    font-weight: 800;
    color: #ffffff;
`
